import { DateTime } from 'luxon';

import { ContactAvatar, TeamUserAvatar } from './Avatar';
import { usePlatformImage } from './contexts';
import { Button } from './Input2';
import { SignalsTooltipLabel } from './SignalsTooltipLabel';
import { cn } from './cn';
import { Communication } from './StrokeIcons';

type ContactCardImageWithOwnerProps = {
  name: string;

  firstName?: string | null;
  lastName?: string | null;
  imageSrc?: string | null;

  /**
   * Trimmed cutout URL (background-removed, transparent edges cropped). When
   * `showCutout` is true and this is set, the card renders a "standing cutout"
   * (transparent, natural aspect, stands on the floor and bleeds above the
   * frame — like the Oracle people list) instead of the circular avatar.
   */
  cutoutSrc?: string | null;
  showCutout?: boolean;

  orgPictureUrl?: string | null;

  cadenceName?: string | null;

  showContactUser?: boolean;
  contactUserProfilePicture?: string | null;
  contactUserLiConnected?: boolean | null;
  contactUserLiConnectedAt?: Date | null;
  contactUserName?: string | null;
  contactUserEmail?: string | null;

  authedUserLiConnected?: boolean | null;
  authedUserLiConnectedAt?: Date | null;

  signalsComputed?: boolean;
  triggeredSignals?: { bucketName: string; reason: string | null; isIgnored?: boolean }[] | null;

  size?: 'default' | 'pipeline';
  isProcessing?: boolean;
};

export function ContactCardImageWithOwner({
  name,
  firstName,
  lastName,
  imageSrc,
  cutoutSrc,
  showCutout,
  orgPictureUrl,
  cadenceName,
  showContactUser,
  contactUserProfilePicture,
  contactUserLiConnected,
  contactUserLiConnectedAt,
  contactUserName,
  contactUserEmail,
  authedUserLiConnected,
  authedUserLiConnectedAt,
  signalsComputed,
  triggeredSignals,
  size = 'default',
  isProcessing,
}: ContactCardImageWithOwnerProps) {
  const avatarSizeClass = size === 'pipeline' ? 'size-11.5' : 'size-17.5';
  const authedConnected = authedUserLiConnected ?? (authedUserLiConnectedAt != null ? true : null);
  const showAuthedDot = !showContactUser && authedConnected === true;
  const showSignalIcon = signalsComputed === true;
  const activeSignals = triggeredSignals?.filter((s) => !s.isIgnored) ?? [];
  const signalsTriggered = activeSignals.length > 0;
  const allSignalsIgnored = (triggeredSignals?.length ?? 0) > 0 && activeSignals.length === 0;
  // Cut transparent circles out of the contact image where badges sit so the
  // card background shows through as a 2px ring. Owner badge (bottom-left):
  // avatar 22/16px at offset -6/-4px, hole radius = avatar radius + 2. Authed
  // dot (bottom-left corner): 10/6px, hole radius = dot radius + 2. Signal
  // icon (top-right): same radius as owner badge.
  const masks: string[] = [];
  if (showContactUser) {
    masks.push(
      size === 'pipeline'
        ? 'radial-gradient(circle at 6px calc(100% - 6px), transparent 10px, #000 10px)'
        : 'radial-gradient(circle at 7px calc(100% - 7px), transparent 13px, #000 13px)',
    );
  } else if (showAuthedDot) {
    masks.push(
      size === 'pipeline'
        ? 'radial-gradient(circle at 0 100%, transparent 5px, #000 5px)'
        : 'radial-gradient(circle at 0 100%, transparent 7px, #000 7px)',
    );
  }
  if (showSignalIcon) {
    masks.push('radial-gradient(circle at calc(100% - 5px) 5px, transparent 12px, #000 12px)');
  }
  const maskStyle: React.CSSProperties | undefined =
    masks.length === 0
      ? undefined
      : {
          maskImage: masks.join(', '),
          WebkitMaskImage: masks.join(', '),
          maskComposite: 'intersect',
          WebkitMaskComposite: 'source-in',
        };

  // Show the standing cutout only when the team flag is on, a trimmed cutout
  // exists, and the photo isn't mid-processing (fall back to the avatar/spinner).
  const cutoutToShow = showCutout === true && isProcessing !== true ? cutoutSrc : null;

  return (
    <div className={cn('relative shrink-0', avatarSizeClass)}>
      {cutoutToShow != null && <ContactCardCutout src={cutoutToShow} alt={name} />}
      {cutoutToShow == null && (
        <ContactAvatar
          firstName={firstName}
          lastName={lastName}
          imageSrc={imageSrc}
          orgImageSrc={orgPictureUrl}
          isProcessing={isProcessing}
          shape="square"
          className="size-full"
          style={maskStyle}
        />
      )}
      {showContactUser && (
        <div
          className={cn(
            'absolute',
            size === 'pipeline' ? '-bottom-0.5 -left-0.5' : '-bottom-1 -left-1',
          )}>
            <TeamUserAvatar
              connected={authedConnected ?? undefined}
              userTeam={{
                profilePicture: contactUserProfilePicture ?? null,
                userEmail: contactUserEmail ?? null,
                userTeamName: contactUserName ?? null,
              }}
              className={cn({ 'size-4': size === 'pipeline', 'size-5.5': size === 'default' })}
              shape="circle"
              tooltip={OwnerTooltipLabel({ contactName: name, cadenceName, contactUserName: contactUserName ?? undefined, contactUserEmail: contactUserEmail ?? undefined, authedLiConnected: contactUserLiConnected ?? undefined, authedLiConnectedAt: contactUserLiConnectedAt ?? undefined })}
            />
        </div>
      )}

      {showAuthedDot && (
        <div
          className={cn(
            'absolute bottom-0 left-0 -translate-x-1/2 translate-y-1/2 rounded-full',
            size === 'pipeline' ? 'size-1.5' : 'size-2.5',
            'bg-mantis-500',
          )}
          title={authedConnectionTooltip(name, authedUserLiConnected, authedUserLiConnectedAt)}
        />
      )}

      {showSignalIcon && (
        <div
          className={cn(
            'absolute top-[5px] right-[5px] -translate-y-1/2 translate-x-1/2',
          )}>
            <Button
              size="2xs"
              color={signalsTriggered ? 'green' : allSignalsIgnored ? 'green' : 'gray'}
              tone={allSignalsIgnored && !signalsTriggered ? 'soft' : 'filled'}
              text={!signalsTriggered && !allSignalsIgnored}
              readOnly
              icon={<Communication.Rss01 />}
              shape='pill'
              className={cn(allSignalsIgnored && 'border border-dashed border-mantis-500/60 opacity-70')}
              tooltipMaxWidth={320}
              tooltip={
                (triggeredSignals?.length ?? 0) > 0 ? (
                  <SignalsTooltipLabel signals={triggeredSignals ?? []} />
                ) : (
                  'Signals computed — none triggered'
                )
              }
            />
        </div>
      )}
    </div>
  );
}

/**
 * The "standing cutout" treatment (like the Oracle people list): a transparent,
 * background-removed contact photo shown at its natural aspect, bottom-aligned so
 * it stands on the image slot's floor and bleeds above the top edge. Rendered in
 * place of the circular avatar when the team's cutout flag is on and a trimmed
 * variant exists. Uses the platform image (next/image on the dashboard) so it
 * still gets optimization — it just isn't clipped to a square like the avatar.
 */
function ContactCardCutout({ src, alt }: { src: string; alt: string }) {
  const Image = usePlatformImage();
  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[150%]" data-testid="contact-card-cutout">
      <Image alt={alt} src={src} fill className="object-contain object-bottom" />
    </div>
  );
}

function authedConnectionTooltip(
  name: string,
  connected: boolean | null | undefined,
  connectedAt: Date | null | undefined,
): string {
  if (connectedAt != null) {
    return `You connected to ${name} on ${DateTime.fromJSDate(connectedAt).toLocaleString(DateTime.DATE_FULL)}`;
  }
  if (connected === true) return `You are connected to ${name}`;
  if (connected === false) return `You are not connected to ${name}`;
  return `Your connection status to ${name} is unknown`;
}

type OwnerTooltipLabelProps = {
  cadenceName: string | null | undefined;
  contactUserName: string | null | undefined;
  contactUserEmail: string | null | undefined;
  authedLiConnected: boolean | null | undefined;
  authedLiConnectedAt: Date | null | undefined;
  contactName: string;
};
function OwnerTooltipLabel({
  cadenceName,
  contactUserName,
  contactUserEmail,
  authedLiConnected,
  authedLiConnectedAt,
  contactName,
}: OwnerTooltipLabelProps) {
  const ownerLine =
    contactUserName ?? contactUserEmail
      ? `${contactUserName ?? contactUserEmail}${cadenceName ? ` running campaign ${cadenceName}` : ''}`
      : undefined;

  const authedLine = authedLiConnectedAt
    ? `You connected to ${contactName} on ${
        authedLiConnectedAt instanceof Date
          ? DateTime.fromJSDate(authedLiConnectedAt).toLocaleString()
          : DateTime.fromMillis(authedLiConnectedAt).toLocaleString()
      }`
    : authedLiConnected === true
      ? `You are connected to ${contactName}`
      : authedLiConnected === false
        ? `You are not connected to ${contactName}`
        : `Your connection status to ${contactName} is unknown`;

  if (!ownerLine) return authedLine;
  return `${authedLine}\n${ownerLine}`;
}
